import React, { useEffect, useState } from 'react';
import { useAnalysisContext, Dataset } from '../../context/AnalysisContext';
import { Database, CheckCircle2, Users, Loader2 } from 'lucide-react';

/**
 * Step 1.5: Dataset Selection (Optional)
 *
 * Features:
 * - Shows only when multiple datasets exist for selected disease
 * - Dataset cards display cell counts and metadata
 * - Single-select radio button interface
 * - "Skip Selection" button to auto-select largest dataset
 */

export const Step1_5_DatasetSelector: React.FC = () => {
  const {
    selectedDisease,
    selectedDatasetId,
    availableDatasets,
    loadingDatasets,
    setSelectedDatasetId,
    fetchDatasetsForDisease,
  } = useAnalysisContext();

  const [hasLoaded, setHasLoaded] = useState(false);

  // ========================================
  // FETCH DATASETS ON MOUNT
  // ========================================

  useEffect(() => {
    if (selectedDisease && !hasLoaded) {
      fetchDatasetsForDisease(selectedDisease);
      setHasLoaded(true);
    }
  }, [selectedDisease, hasLoaded, fetchDatasetsForDisease]);

  // ========================================
  // HANDLERS
  // ========================================

  const handleSelectDataset = (id: string) => {
    setSelectedDatasetId(id);
  };

  const handleSkipSelection = () => {
    // Auto-select largest dataset
    if (availableDatasets.length > 0) {
      const largest = availableDatasets.reduce((prev, current) =>
        current.total_cells > prev.total_cells ? current : prev
      );
      setSelectedDatasetId(largest.id);
    }
  };

  const truncateId = (id: string, maxLength: number = 20): string => {
    if (id.length <= maxLength) return id;
    return `${id.substring(0, maxLength - 3)}...`;
  };

  // ========================================
  // LOADING STATE
  // ========================================

  if (loadingDatasets) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Loader2 className="w-12 h-12 text-[var(--origin-blue-500)] animate-spin" />
        <p className="text-lg text-[var(--text-secondary)]">
          Loading available datasets for {selectedDisease}...
        </p>
      </div>
    );
  }

  // ========================================
  // NO DATASETS OR ONLY ONE DATASET
  // ========================================

  if (availableDatasets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Database className="w-16 h-16 text-[var(--text-tertiary)]" />
        <p className="text-lg text-[var(--text-secondary)]">
          No datasets found for {selectedDisease}
        </p>
        <p className="text-sm text-[var(--text-tertiary)] max-w-md text-center">
          This may indicate a data availability issue. Please try selecting a different disease
          or contact support.
        </p>
      </div>
    );
  }

  if (availableDatasets.length === 1) {
    // Auto-select the only dataset
    if (!selectedDatasetId) {
      setSelectedDatasetId(availableDatasets[0].id);
    }

    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <CheckCircle2 className="w-16 h-16 text-[var(--success)]" />
        <p className="text-lg text-[var(--text-primary)]">
          Dataset automatically selected
        </p>
        <div className="origin-card p-4 max-w-md">
          <p className="text-sm text-[var(--text-secondary)]">
            <strong>Dataset:</strong> {truncateId(availableDatasets[0].id)}
          </p>
          <p className="text-sm text-[var(--text-secondary)] mt-2">
            <strong>Total Cells:</strong> {availableDatasets[0].total_cells.toLocaleString()}
          </p>
        </div>
      </div>
    );
  }

  // ========================================
  // MULTIPLE DATASETS - SHOW SELECTOR
  // ========================================

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
          Select Dataset
        </h2>
        <p className="text-[var(--text-secondary)]">
          Multiple datasets are available for <strong>{selectedDisease}</strong>.
          Choose the dataset that best fits your analysis needs.
        </p>
      </div>

      {/* Dataset Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {availableDatasets.map((dataset) => {
          const isSelected = selectedDatasetId === dataset.id;

          return (
            <button
              key={dataset.id}
              onClick={() => handleSelectDataset(dataset.id)}
              className={`
                origin-card p-6 text-left transition-all duration-200
                ${
                  isSelected
                    ? 'border-2 border-[var(--origin-blue-500)] bg-[var(--origin-blue-500)]/5'
                    : 'border-2 border-transparent hover:border-[var(--border-secondary)]'
                }
              `}
            >
              {/* Radio indicator */}
              <div className="flex items-start justify-between mb-4">
                <Database className="w-8 h-8 text-[var(--origin-blue-500)]" />
                <div
                  className={`
                    w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                    ${
                      isSelected
                        ? 'border-[var(--origin-blue-500)] bg-[var(--origin-blue-500)]'
                        : 'border-[var(--border-primary)]'
                    }
                  `}
                >
                  {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                </div>
              </div>

              {/* Dataset ID */}
              <div className="mb-4">
                <p
                  className="text-xs text-[var(--text-tertiary)] mb-1"
                  title="Dataset Identifier"
                >
                  Dataset ID
                </p>
                <p
                  className="font-mono text-sm text-[var(--text-primary)] truncate"
                  title={dataset.id}
                >
                  {truncateId(dataset.id, 30)}
                </p>
              </div>

              {/* Cell Counts */}
              <div className="space-y-3">
                {/* Total Cells */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[var(--text-tertiary)]" />
                    <span className="text-sm text-[var(--text-secondary)]">Total Cells</span>
                  </div>
                  <span className="text-sm font-semibold text-[var(--text-primary)]">
                    {dataset.total_cells.toLocaleString()}
                  </span>
                </div>

                {/* Disease Cells */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[var(--error)]" />
                    <span className="text-sm text-[var(--text-secondary)]">Disease Cells</span>
                  </div>
                  <span className="text-sm font-semibold text-[var(--error)]">
                    {dataset.n_disease_cells.toLocaleString()}
                  </span>
                </div>

                {/* Normal Cells */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[var(--success)]" />
                    <span className="text-sm text-[var(--text-secondary)]">Normal Cells</span>
                  </div>
                  <span className="text-sm font-semibold text-[var(--success)]">
                    {dataset.n_normal_cells.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Optional Metadata */}
              {(dataset.collection_date || dataset.source) && (
                <div className="mt-4 pt-4 border-t border-[var(--border-primary)] space-y-1">
                  {dataset.collection_date && (
                    <p className="text-xs text-[var(--text-tertiary)]">
                      <strong>Date:</strong> {dataset.collection_date}
                    </p>
                  )}
                  {dataset.source && (
                    <p className="text-xs text-[var(--text-tertiary)]">
                      <strong>Source:</strong> {dataset.source}
                    </p>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Skip Selection Button */}
      <div className="flex justify-center mt-8">
        <button
          onClick={handleSkipSelection}
          className="origin-btn origin-btn-ghost text-sm"
        >
          Skip Selection (Auto-select Largest)
        </button>
      </div>

      {/* Helper Text */}
      <div className="mt-6 p-4 bg-[var(--bg-secondary)] rounded-lg">
        <p className="text-sm text-[var(--text-secondary)]">
          <strong>Tip:</strong> Larger datasets generally provide more statistical power, but may
          take longer to process. Consider your analysis needs when selecting.
        </p>
      </div>
    </div>
  );
};

export default Step1_5_DatasetSelector;
